---
title: "Boolean search syntax"
description: "Boolean candidate search in Chosen — AND, OR, NOT, quoted phrases, and parentheses. A practical cheat-sheet with copy-pasteable examples that actually work."
category: "search"
keywords:
  - "boolean search syntax"
  - "boolean candidate search"
  - "AND OR NOT search"
  - "boolean search operators"
  - "exact phrase search ATS"
schema: "HowTo"
nav:
  section: "guides"
  order: 20
  parent: "/guides/search"
---

Boolean search runs in the standard query bar — type the operators and Chosen matches them literally. Use it when smart search is being too generous and you want exact control: `AND`, `OR`, `NOT`, double quotes for exact phrases, parentheses to group. Operators must be uppercase.

## The operators

| Operator | What it does | Example |
| --- | --- | --- |
| `AND` | Both terms must appear | `react AND typescript` |
| `OR` | Either term can appear | `python OR rust` |
| `NOT` | Excludes the term that follows | `react NOT contractor` |
| `"..."` | Matches the exact phrase | `"senior engineer"` |
| `( )` | Groups terms so an operator applies to all of them | `(NYC OR remote)` |

`AND`, `OR`, and `NOT` only count as operators when they are uppercase. Lowercase `and` is treated as an ordinary search word.

## Quoted phrases

Without quotes, `senior engineer` matches candidates with "senior" *and* "engineer" anywhere — including a senior designer who once mentored an engineer. Double quotes force the words to appear together, in order.

```
"senior engineer"
"product manager"
"machine learning"
```

Reach for quotes whenever a role title is two or more words and the individual words are common.

## Parentheses and precedence

Parentheses group terms so an operator applies to the whole group. Without them, mixing `AND` and `OR` gets ambiguous fast.

```
"senior engineer" AND payments AND (NYC OR remote)
```

That reads as: a senior engineer, with payments, who is in NYC *or* remote. Drop the parentheses and the grouping is no longer something you can rely on. When a query mixes `AND` and `OR`, parenthesize the `OR` part — every time.

## Examples worth copying

```
"senior engineer" AND payments AND (NYC OR remote)
```

Senior engineers with payments experience, in NYC or working remotely.

```
react NOT contractor
```

React candidates, excluding anyone whose resume says contractor.

```
(python OR golang) AND (kubernetes OR docker)
```

A backend language and a container tool — any combination of the two.

```
"product designer" AND (fintech OR healthcare) NOT agency
```

Product designers in one of two domains, excluding agency backgrounds.

## When boolean beats smart search

Smart search reads intent, which is usually what you want. Boolean wins in two situations:

- **The phrase matters.** Smart search matches "senior" and "engineer" as separate signals. Quotes make the bar treat `"senior engineer"` as one thing.
- **You want a hard exclude.** `NOT contractor` removes those candidates outright. Smart search treats your wording as a preference, not a rule, so it may still surface a contractor it thinks is a strong match.

If you are describing a *kind* of person — "engineers who led a small team" — that is qualitative, and smart search or [deep search](/guides/search/smart-vs-deep) handles it better than boolean ever will.

## Limits

Boolean matches the text that exists on a candidate. If a resume came in as an image-only PDF, [resume parsing](/guides/candidates/resume-parsing) couldn't extract the text, so a boolean query has nothing to match against. Boolean is exact, not clairvoyant — a candidate who wrote "K8s" will not match `kubernetes`. When a query returns nothing, [why a search returns no results](/guides/search/why-no-results) covers the usual causes.
